// Ensure all dependencies are correctly imported
import { $Qll } from 'graditify-utils';
import api from '../services/api';
import { $Q } from '../utils/query-selector';
import { stringToHTML } from '../utils/to-html';
import { dataToggle, isActive } from '../utils/toggle-dataset';
import { attr } from '../utils/validate-attr';

/**
 * Delay function
 *
 * @param {Function} fn - Function that you want a delay execution
 * @param {Number} wait - This time delay (ms)
 * @returns Function delay
 */
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function resetForm() {
  const results = $Q('#predictive-search-results');
  const searchBar = $Q('#Search');

  if (results && searchBar) {
    results.remove();
    searchBar.value = '';
  }
}

/**
 * Toggle predictive search bar
 * @param {String} control - Button to show predictive search
 * @param {HTMLElement} node - Predictive search element
 * @param {Object} config - Toggle configuration
 * @returns {VoidFunction} - Predictive show or hide
 */

function initializeSearchState(predictive, iconSearch, iconClose) {
  if (!isActive(predictive.dataset)) {
    iconSearch.classList.remove('hidden');
    iconClose.classList.add('hidden');
    resetForm();
  } else {
    iconSearch.classList.add('hidden');
    iconClose.classList.remove('hidden');
  }
}

function toggleSearchBar(control, predictive, config = {}) {
  const header = $Q('#header-js');
  const trigger = control ? $Q(control) : null;

  if (!trigger) return;

  const iconSearch = $Q('svg:first-child', trigger);
  const iconClose = $Q('svg:last-child', trigger);
  const input = $Q('#Search', predictive);

  const { overlay, cb } = config;

  document.addEventListener('keydown', (e) => {
    const key = e.code;
    if (key === 'Escape') {
      const active = isActive(predictive.dataset);

      if (active) {
        dataToggle(predictive, overlay, cb);
        if (!window.location.href.includes('search')) {
          header.classList.remove('header--normal');
        }
        resetForm();
      }
    }
  });

  trigger.addEventListener('click', () => {
    const active = isActive(predictive.dataset);
    if (!active) {
      dataToggle(predictive, overlay, cb);
      if (!window.location.href.includes('search')) {
        header.classList.add('header--normal');
      } else {
        predictive.classList.add('absoluted-positioned');
      }
      iconSearch.classList.add('hidden');
      iconClose.classList.remove('hidden');
      input.focus();
    } else {
      dataToggle(predictive, overlay, cb);
      if (!window.location.href.includes('search')) {
        header.classList.remove('header--normal');
      } else {
        predictive.classList.remove('absoluted-positioned');
      }
      iconSearch.classList.remove('hidden');
      iconClose.classList.add('hidden');
      resetForm();
    }
  });

  // Initialize search state on load
  initializeSearchState(predictive, iconSearch, iconClose);
}

function handleSearchForm() {
  if (window.location.href.includes('search')) {
    const form = $Q('.filter-form');
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const entries = urlParams.entries();

    for (const entry of entries) {
      const [name, value] = entry;
      const node = document.createElement('input');
      node.type = 'hidden';
      node.name = name;
      node.value = value;
      node.className = 'search-original';

      form.prepend(node);
    }
  }
}

function handleClearBtn() {
  if (window.location.href.includes('search')) {
    const searchList = $Qll('.search-original');
    const clearBtn = $Q('.active-filters__clear');
    let url = `${window.location.origin}${window.location.pathname}?`;

    if (!clearBtn) return;

    searchList.forEach(({ name, value }) => {
      if (name === 'q' || name === 'options[prefix]' || name === 'type') {
        url += `${name}=${value}&`;
      }
    });

    clearBtn.href = new URL(url).href;
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.params = attr.call(this, 'params') || 'product';
    this.fields = attr.call(this, 'fields') || 'title';
    this.limit = attr.call(this, 'limit') || 3;
    this.resultsWrapper = $Q('#predictive-results-js', this);
  }

  connectedCallback() {
    const input = $Q("input[type='search']", this);
    toggleSearchBar('.search-js', this, {
      overlay: false,
    });

    input.addEventListener(
      'input',
      debounce((event) => {
        this.onChangePredictive(event);
      }, 300).bind(this)
    );
  }

  /**
   *
   * @param {Event} event - event input change in field search
   * if is text letter is open and execute query fetch api, else close suggest
   */
  onChangePredictive(event) {
    if (event.target.value.length < 3) return;
    const searchTerm = event.target.value.trim();
    const searchBtn = $Q('.find-search-js', this);

    if (searchBtn) {
      searchBtn.disabled = false;
    }

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.open();

    this.resultsWrapper.innerHTML = `
      <div class="loading-container">
        <div id='loading'></div>
      <div>
    `;

    this.getSearchResults(searchTerm);
  }

  /**
   * get list result for query api.
   * @param {String} searchTerm - query search
   */
  async getSearchResults(searchTerm) {
    const base = `${routes.predictive_search_url}?q=${searchTerm}&resources[type]=${this.params}&resources[limit]=${this.limit}&resources[limit_scope]=each&resources[options][fields]=${this.fields}`;

    const result = await api.shopifySectionByUrl(
      base,
      'predictive-search',
      '&section_id'
    );

    const listItems = $Q('#predictive-search-results', stringToHTML(result));
    this.resultsWrapper.innerHTML = listItems.outerHTML;
  }

  /**
   * Show wrapper of results
   */
  open() {
    this.resultsWrapper.style.display = 'block';
  }

  /**
   * Hide wrapper of results
   */
    close() {
    this.resultsWrapper.style.display = 'none';
  }
}

/* const input = document.querySelector(".clear-input")

const handleInputChange = (e) => {
  if (e.target.value && !input.classList.contains("clear-input--touched")) {
    input.classList.add("clear-input--touched")
  } else if (!e.target.value && input.classList.contains("clear-input--touched")) {
    input.classList.remove("clear-input--touched")
  }
}

input.addEventListener("input", handleInputChange) */

export { PredictiveSearch, toggleSearchBar, handleSearchForm, handleClearBtn };
